import React from "react";

import {
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiFolder,
  FiGitBranch,
  FiPlus,
  FiPower,
  FiTrash2,
} from "react-icons/fi";


/* =========================================================
   HELPERS
========================================================= */

const normalizeId = (
  value
) => {
  if (!value) {
    return "";
  }

  if (
    typeof value ===
    "string"
  ) {
    return value;
  }

  if (
    typeof value ===
    "object"
  ) {
    return (
      value._id ||
      value.id ||
      ""
    );
  }

  return String(
    value
  );
};


const getParentName = (
  category
) => {
  const parent =
    category?.parentCategory;

  if (!parent) {
    return "-";
  }

  if (
    typeof parent ===
    "object"
  ) {
    return (
      parent.name ||
      "-"
    );
  }

  return "-";
};


const getLevelLabel = (
  level
) => {
  const value =
    Number(
      level ||
      0
    );

  if (
    value ===
    0
  ) {
    return "Root";
  }

  return `Level ${value}`;
};


const getLevelClass = (
  level
) => {
  const value =
    Number(
      level ||
      0
    );

  if (
    value ===
    0
  ) {
    return "inventory-category-level-root";
  }

  if (
    value ===
    1
  ) {
    return "inventory-category-level-one";
  }

  if (
    value ===
    2
  ) {
    return "inventory-category-level-two";
  }

  return "inventory-category-level-deep";
};


const getStatusClass = (
  status
) => {
  return status ===
    "active"
    ? "inventory-category-status-active"
    : "inventory-category-status-inactive";
};


const getNextStatus = (
  status
) => {
  return status ===
    "active"
    ? "inactive"
    : "active";
};


/* =========================================================
   FORMAT DATE
========================================================= */

const formatDate = (
  value
) => {
  if (!value) {
    return "-";
  }

  const date =
    new Date(
      value
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }

  return new Intl.DateTimeFormat(
    "en-IN",
    {
      day:
        "2-digit",

      month:
        "short",

      year:
        "numeric",
    }
  ).format(
    date
  );
};


/* =========================================================
   CATEGORY TABLE
========================================================= */

const CategoryTable = ({
  categories = [],

  loading = false,

  pagination,

  onEdit,

  onDelete,

  onChangeStatus,

  onPageChange,

  onLimitChange,

  onAddCategory,

  onAddChild,
}) => {

  /* =======================================================
     PAGINATION
  ======================================================= */

  const safePagination = {
    page:
      Number(
        pagination?.page ||
        1
      ),

    limit:
      Number(
        pagination?.limit ||
        10
      ),

    total:
      Number(
        pagination?.total ||
        pagination?.totalRecords ||
        0
      ),

    totalPages:
      Math.max(
        Number(
          pagination?.totalPages ||
          1
        ),
        1
      ),
  };


  /* =======================================================
     SERIAL NUMBER
  ======================================================= */

  const getSerialNumber =
    (
      index
    ) => {
      return (
        (
          safePagination.page -
          1
        ) *
          safePagination.limit +
        index +
        1
      );
    };


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="inventory-category-table-card"
    >

      {/* ===================================================
          TABLE
      =================================================== */}

      <div
        className="inventory-category-table-scroll"
      >
        <table
          className="inventory-category-table"
        >
          <thead>
            <tr>

              <th
                style={{
                  width:
                    65,
                }}
              >
                S.No
              </th>


              <th>
                Category
              </th>


              <th>
                Hierarchy
              </th>


              <th>
                Parent
              </th>


              <th>
                Slug
              </th>


              <th>
                Sort
              </th>


              <th>
                Status
              </th>


              <th>
                Created On
              </th>


              <th
                style={{
                  width:
                    210,
                }}
              >
                Actions
              </th>

            </tr>
          </thead>


          <tbody>

            {/* =============================================
                LOADING
            ============================================= */}

            {loading ? (

              <tr>
                <td
                  colSpan={
                    9
                  }
                  className="inventory-category-table-message"
                >
                  <div
                    className="inventory-category-table-loading"
                  >
                    <span
                      className="inventory-category-table-spinner"
                    />

                    Loading categories...
                  </div>
                </td>
              </tr>

            ) : categories.length ===
              0 ? (

              /* ===========================================
                 EMPTY
              =========================================== */

              <tr>
                <td
                  colSpan={
                    9
                  }
                  className="inventory-category-table-message"
                >
                  <div
                    className="inventory-category-table-empty"
                  >

                    <FiFolder />


                    <strong>
                      No Categories Found
                    </strong>


                    <span>
                      Create categories and
                      subcategories to organize
                      your products.
                    </span>


                    <button
                      type="button"
                      onClick={
                        onAddCategory
                      }
                    >
                      <FiPlus />

                      Add Category
                    </button>

                  </div>
                </td>
              </tr>

            ) : (

              /* ===========================================
                 ROWS
              =========================================== */

              categories.map(
                (
                  category,
                  index
                ) => {

                  const level =
                    Number(
                      category?.level ||
                      0
                    );


                  const parentName =
                    getParentName(
                      category
                    );


                  const isRoot =
                    level ===
                    0;


                  const indentation =
                    Math.min(
                      level,
                      6
                    ) *
                    18;


                  return (
                    <tr
                      key={
                        category?._id ||
                        index
                      }
                      className={
                        isRoot
                          ? "inventory-category-row-root"
                          : "inventory-category-row-child"
                      }
                    >

                      {/* ===================================
                          SERIAL
                      =================================== */}

                      <td
                        className="inventory-category-table-serial"
                      >
                        {
                          getSerialNumber(
                            index
                          )
                        }
                      </td>


                      {/* ===================================
                          CATEGORY
                      =================================== */}

                      <td>

                        <div
                          className="inventory-category-table-name"
                          style={{
                            paddingLeft:
                              `${indentation}px`,
                          }}
                        >

                          {/* TREE MARKER */}

                          {!isRoot && (
                            <span
                              className="inventory-category-tree-marker"
                              aria-hidden="true"
                            >
                              ↳
                            </span>
                          )}


                          {/* IMAGE / ICON */}

                          <div
                            className="inventory-category-table-icon"
                          >

                            {category?.image ? (

                              <img
                                src={
                                  category.image
                                }
                                alt={
                                  category.name ||
                                  "Category"
                                }
                                onError={
                                  (
                                    event
                                  ) => {
                                    event.currentTarget.style.display =
                                      "none";
                                  }
                                }
                              />

                            ) : (

                              <FiFolder />

                            )}

                          </div>


                          {/* NAME */}

                          <div
                            className="inventory-category-table-name-content"
                          >

                            <div
                              className="inventory-category-name-line"
                            >

                              <strong
                                title={
                                  category?.name
                                }
                              >
                                {
                                  category?.name ||
                                  "-"
                                }
                              </strong>


                              {isRoot && (

                                <span
                                  className="inventory-category-root-badge"
                                >
                                  ROOT
                                </span>

                              )}

                            </div>


                            <span
                              title={
                                category
                                  ?.description
                              }
                            >
                              {
                                category
                                  ?.description ||
                                "No description"
                              }
                            </span>

                          </div>

                        </div>

                      </td>


                      {/* ===================================
                          LEVEL
                      =================================== */}

                      <td>
                        <span
                          className={`inventory-category-level-badge ${getLevelClass(
                            level
                          )}`}
                        >
                          <FiGitBranch />

                          {
                            getLevelLabel(
                              level
                            )
                          }
                        </span>
                      </td>


                      {/* ===================================
                          PARENT
                      =================================== */}

                      <td>

                        {isRoot ? (

                          <span
                            className="inventory-category-no-parent"
                          >
                            —
                          </span>

                        ) : (

                          <span
                            className="inventory-category-parent-name"
                            title={
                              category?.parentCategory
                                ?.name ||
                              parentName
                            }
                          >
                            {
                              parentName
                            }
                          </span>

                        )}

                      </td>


                      {/* ===================================
                          SLUG
                      =================================== */}

                      <td>
                        <code
                          className="inventory-category-slug"
                          title={
                            category?.slug
                          }
                        >
                          {
                            category?.slug ||
                            "-"
                          }
                        </code>
                      </td>


                      {/* ===================================
                          SORT
                      =================================== */}

                      <td>
                        <span
                          className="inventory-category-sort-order"
                        >
                          {
                            Number(
                              category?.sortOrder ||
                              0
                            )
                          }
                        </span>
                      </td>


                      {/* ===================================
                          STATUS
                      =================================== */}

                      <td>

                        <button
                          type="button"
                          className={`inventory-category-status ${getStatusClass(
                            category?.status
                          )}`}
                          title={
                            category?.status ===
                            "active"
                              ? "Click to deactivate"
                              : "Click to activate"
                          }
                          onClick={
                            () =>
                              onChangeStatus?.(
                                category,
                                getNextStatus(
                                  category?.status
                                )
                              )
                          }
                        >
                          {
                            category?.status ===
                            "active"
                              ? "Active"
                              : "Inactive"
                          }
                        </button>

                      </td>


                      {/* ===================================
                          CREATED
                      =================================== */}

                      <td>
                        <span
                          className="inventory-category-created-date"
                        >
                          {
                            formatDate(
                              category
                                ?.createdAt
                            )
                          }
                        </span>
                      </td>


                      {/* ===================================
                          ACTIONS
                      =================================== */}

                      <td>

                        <div
                          className="inventory-category-actions"
                        >

                          {/* ADD CHILD */}

                          <button
                            type="button"
                            className="
                              inventory-category-action
                              inventory-category-action-child
                            "
                            title={`Add subcategory under ${
                              category?.name ||
                              "category"
                            }`}
                            onClick={
                              () =>
                                onAddChild?.(
                                  category
                                )
                            }
                          >
                            <FiPlus />
                          </button>


                          {/* EDIT */}

                          <button
                            type="button"
                            className="
                              inventory-category-action
                              inventory-category-action-edit
                            "
                            title="Edit category"
                            onClick={
                              () =>
                                onEdit?.(
                                  category
                                )
                            }
                          >
                            <FiEdit2 />
                          </button>


                          {/* STATUS */}

                          <button
                            type="button"
                            className="
                              inventory-category-action
                              inventory-category-action-status
                            "
                            title={
                              category?.status ===
                              "active"
                                ? "Deactivate category"
                                : "Activate category"
                            }
                            onClick={
                              () =>
                                onChangeStatus?.(
                                  category,
                                  getNextStatus(
                                    category?.status
                                  )
                                )
                            }
                          >
                            <FiPower />
                          </button>


                          {/* DELETE */}

                          <button
                            type="button"
                            className="
                              inventory-category-action
                              inventory-category-action-delete
                            "
                            title="Delete category"
                            onClick={
                              () =>
                                onDelete?.(
                                  category
                                )
                            }
                          >
                            <FiTrash2 />
                          </button>

                        </div>

                      </td>

                    </tr>
                  );
                }
              )

            )}

          </tbody>
        </table>
      </div>


      {/* ===================================================
          PAGINATION
      =================================================== */}

      {!loading &&
      safePagination.total >
        0 ? (

        <div
          className="inventory-category-pagination"
        >

          {/* SUMMARY */}

          <div
            className="inventory-category-pagination-summary"
          >

            Showing{" "}

            <strong>
              {
                (
                  safePagination.page -
                  1
                ) *
                  safePagination.limit +
                1
              }
            </strong>

            {" "}to{" "}

            <strong>
              {
                Math.min(
                  safePagination.page *
                    safePagination.limit,

                  safePagination.total
                )
              }
            </strong>

            {" "}of{" "}

            <strong>
              {
                safePagination.total
              }
            </strong>

            {" "}categories

          </div>


          {/* CONTROLS */}

          <div
            className="inventory-category-pagination-controls"
          >

            <select
              value={
                safePagination.limit
              }
              onChange={
                onLimitChange
              }
              aria-label="Categories per page"
            >

              <option
                value={
                  10
                }
              >
                10 / page
              </option>


              <option
                value={
                  20
                }
              >
                20 / page
              </option>


              <option
                value={
                  50
                }
              >
                50 / page
              </option>


              <option
                value={
                  100
                }
              >
                100 / page
              </option>

            </select>


            {/* PREVIOUS */}

            <button
              type="button"
              disabled={
                safePagination.page <=
                1
              }
              onClick={
                () =>
                  onPageChange?.(
                    safePagination.page -
                    1
                  )
              }
              title="Previous page"
            >
              <FiChevronLeft />
            </button>


            <span>
              Page{" "}

              <strong>
                {
                  safePagination.page
                }
              </strong>

              {" "}of{" "}

              <strong>
                {
                  safePagination.totalPages
                }
              </strong>
            </span>


            {/* NEXT */}

            <button
              type="button"
              disabled={
                safePagination.page >=
                safePagination.totalPages
              }
              onClick={
                () =>
                  onPageChange?.(
                    safePagination.page +
                    1
                  )
              }
              title="Next page"
            >
              <FiChevronRight />
            </button>

          </div>

        </div>

      ) : null}

    </div>
  );
};


export default CategoryTable;